import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { App, Button, Dropdown, Modal } from 'antd';

import { api } from '../../api/client';
import { API_ITEMS_URL } from '../../utils';
import { normalizeId } from '../../utils/ids';
import { rowOwnCollection } from '../../utils/recordQualifier';
import { cardPagePath } from '../../utils/cards';
import getItemDisplayValue from '../../utils/items';
import { buildItemShareUrl } from '../../utils/shareUrls';
import { createItemRelationship } from '../../services/itemRelationships';
import { followItem, followedElementsText, followRefusal, getItemFollow, unfollowItem } from '../../services/itemFollows';
import { getItemAttachments } from '../../services/attachments';
import { PIN_KIND, TILE_SOURCE } from '../../services/favorites';
import RecordTileModal from '../home/RecordTileModal';
import { useOptionalFavorites } from '../../contexts/FavoritesContext';
import { buildActionMenu } from '../common/actionMenu';
import { confirmDestructive } from '../common/confirmDestructive';
import PrintLayoutPicker from '../layouts/PrintLayoutPicker';
import { useFilterByValue, useValueFacets } from '../common/FilterByValueContext';
import { describeValueFilter } from '../../utils/filterValues';
import { itemValueFilters } from './itemFilterValues';
import AttachmentsSection from '../common/AttachmentsSection';
import ExportModal from '../common/ExportModal';
import RecordBadge, { RecordIdentity } from '../common/RecordBadge';
import HistoryModal from '../history/HistoryModal';
import { fetchItemHistory } from '../../services/history';
import { HISTORY, KEBAB, LIGHTNING } from '../icons';
import LibraryIcon from '../common/LibraryIcon';
import CardActionInputsModal from './CardActionInputsModal';
import useCardActions from '../../hooks/useCardActions';
import useRecordCardActions from '../../hooks/useRecordCardActions';
import { pressCardAction } from '../../services/cardActions';
import { actionLabel, actionStanding, actionsForCard, anyConditional, isNotApplicable, newPressKey, partialSentence, pressRefusal, pressSentence, summarizeWritten } from '../../utils/cardActions';
import ItemShareModal from './ItemShareModal';
import ItemAudienceModal from './ItemAudienceModal';
import QuickAddTemplateModal from './QuickAddTemplateModal';
import ItemCorrectionModal from './ItemCorrectionModal';
import ItemTagsModal from '../tags/ItemTagsModal';
import ItemUnlockModal from './ItemUnlockModal';
import TroubleshootWalkthrough from './TroubleshootWalkthrough';
import { isSymptom } from '../../utils/troubleshooting';
import RelateItemModal from './RelateItemModal';
import { cardLocksRecords, lockStateFor, recordPolicyFor } from '../../utils/recordPolicy';
import { useOptionalCardDefinitions } from '../../contexts/CardDefinitionsContext';
import useDuplicateProfiles from '../../hooks/useDuplicateProfiles';
import { defaultDuplicateProfile, launchDuplicateProfile, selectDuplicateProfiles } from '../../utils/duplicateProfiles';
import { linkReaderExclusions, linkedReadOnlyReason } from '../../utils/linkedRecords';
import { CHANGE_CARD_LABEL, changeCardPath, itemChangeCardState } from '../../utils/changeCardEntry';
import useCollectionTags from '../../hooks/useCollectionTags';
import { useOptionalTags } from '../../contexts/TagsContext';
import { itemTagEntries } from '../../utils/itemTags';
import { rememberedItemTagTitles } from '../../utils/tagTitleMemory';
import useItemOwnTag from '../../hooks/useItemOwnTag';
import useRecordCodesVersion from '../../hooks/useRecordCodes';
import CopyItemModal from './CopyItemModal';
import { ItemDeleteBody, duplicateItemNavigation, itemDeleteConfirmText } from './itemActionsHelpers';
import useStandInCopy from '../../hooks/useStandInCopy';

export { duplicateItemNavigation, itemDeleteConfirmText } from './itemActionsHelpers';

// A stable default, so the memoized exclusion list does not change every render.
// Callers pass this, never a literal `[]` (KPR-932).
export const NO_EXCLUSIONS = Object.freeze([]);

/**
 * THE item action set, in the platform taxonomy — one definition shared by
 * every surface an item appears on (list card, detail pane, inline detail,
 * entry form), so no view ever offers fewer verbs than another:
 *
 *   navigate     Open item · Start walk-through (a Symptom only) · Go to card definition
 *   record       Duplicate · Duplicate as… · Copy to… · Change card · Show my copy instead of the original | Show the original again · Save as quick add… · Pin to Home · Pin as tile… · Follow | Unfollow · Tag… · Use as a tag | Stop using as a tag… · Filter by… · Relate item… · Attachments… · Who can see this · History…
 *   share        Share item · Export…
 *   destructive  Delete item…
 *
 * "Filter by…" is the one action whose object is a FIELD rather than the
 * record, so it is the one action with a submenu: the item's filterable values,
 * each of which narrows the list this surface sits in. It appears only where
 * there is such a list (a FilterByValueProvider above), which is why a row in
 * the items table offers it and the item's own route does not.
 *
 * DUPLICATE runs the card's default duplicate profile when a manager set one
 * (utils/duplicateProfiles.js — the nearest card's `defaultForDuplicate`),
 * else plain Duplicate. When the card has any profiles, "Duplicate as…"
 * lists them all plus "Plain duplicate", and `duplicateMenu` is the same
 * list for a surface that shows Duplicate as a labeled split button (the
 * detail pane's Duplicate ▾).
 *
 * Returns the antd `menu` for a ⋯ trigger, the modals the actions open (render
 * them once, anywhere in the surface), the raw action list, and `run(key)` for
 * surfaces that promote an action to a labeled button (the pane's "Open item").
 *
 * `surface` hides what makes no sense where you are ('form' drops Open item —
 * you are on it). `exclude` drops keys explicitly. Capability gates mirror the
 * API: `canEdit` (write on the item's card), `collection.allowAttachments`.
 *
 * TAG… is offered to everyone who can read the item (docs/plans/tags.md §5.2):
 * private tags need read access only, so it is never gated on `canEdit` — the
 * dialog greys the collection tags instead. It opens ItemTagsModal, which saves
 * as it goes and hands the item back through `onItemUpdated`; a surface with a
 * tag row of its own (the form, whose tags save with it) passes `onOpenTags`
 * to open that instead.
 *
 * USE AS A TAG / STOP USING AS A TAG… (T4d, plan §5.2) follow Tag…: a manager
 * of the item's collection, on a card in Chosen items mode there — the rule
 * lives in hooks/useItemOwnTag.js, shared with the read view. A reader never
 * sees either, and never makes the request that asks.
 */

export function useItemActions({
	item,
	card,
	elementDefs,
	resolveLookupValue,
	collectionId,
	collection = null,
	canEdit = true,
	// Manage on the item's collection: unlocking a locked record is a
	// manager's act, and the API says so whatever this menu offers.
	canManage = false,
	// The card cache, for the record policy a card inherits from its parent.
	cardsById = null,
	surface = 'pane',
	exclude: excludeProp = NO_EXCLUSIONS,
	title: titleOverride,
	onDeleted,
	onRelated,
	onOpenAttachments,
	onOpenRelated,
	// Tag…: the item as it stands after a tag change (the list's feed puts it
	// in the row's place), and — on a surface with its own tag row — the door
	// to that row instead of the dialog.
	onItemUpdated,
	onOpenTags,
	// A card action was pressed on this record (KPR-551): the surface that
	// holds the record in its own state reloads it.
	onPressed,
	// A manager unlocked the record, with the unlock's answer ({ ok,
	// unlockedUntil }). Without it the surface reloads its related view, as
	// before; the item form opens itself instead (KPR-155).
	onUnlocked,
	// The surface knows the record is locked though its dates say otherwise
	// (the item form, after a save the API refused with 409 item_locked).
	locked: lockedOverride = null,
	// Entries the surface adds for this record (KPR-1396: a list in a manual
	// order adds Move up, Move down and Move to top), first in their groups.
	extraActions = null,
}) {
	const navigate = useNavigate();
	const { message, modal } = App.useApp();
	// Null everywhere but inside a filterable list.
	const applyValueFilter = useFilterByValue();
	// Which lookups the list's vocabulary covers with a facet — null outside
	// a list, and on a list whose collection has none.
	const facetFor = useValueFacets();
	// Pin to Home. Optional: surfaces rendered without the provider (tests,
	// the public shared page) simply do not offer it.
	const favorites = useOptionalFavorites();
	// Which modal is open, and which have ever opened (mounted on first use
	// and kept, so each gets its close animation and a list of fifty cards
	// doesn't mount two hundred idle modals).
	const [activeModal, setActiveModal] = useState(null);
	const [mounted, setMounted] = useState(() => new Set());
	const [relating, setRelating] = useState(false);

	// A link-only reader of this collection (reading A's record through a
	// collection link) is offered only what the API lets a link reader do,
	// on every surface — not just where the row is marked as linked.
	const exclude = useMemo(() => linkReaderExclusions(collection, excludeProp), [collection, excludeProp]);
	const itemId = normalizeId(item?._id);
	const saved = Boolean(itemId) && itemId !== '-1';
	const cardId = normalizeId(item?.card_id || card?._id);
	const cardType = card?.label?.singular || card?.name || 'Item';
	const title = titleOverride ?? getItemDisplayValue({ card, elementDefs, item, resolveLookupValue });
	const pinned = Boolean(favorites?.isPinned && saved && favorites.isPinned(PIN_KIND.ITEM, itemId));
	// A tile of this record's values on Home (KPR-1127): pinned or not.
	const tileTarget = useMemo(() => ({ source: TILE_SOURCE.RECORD, targetId: itemId, collectionId: normalizeId(item?.collection_id) || normalizeId(collectionId) }), [itemId, item?.collection_id, collectionId]);
	const tiled = Boolean(saved && favorites?.tileFor && Array.isArray(favorites.tileFor(tileTarget)));

	// FOLLOW (keepr-api docs/SCHEMA.md § Following an item; KPR-391): "tell
	// me when this changes". Asked only on the item's OWN surfaces — the
	// detail pane and the item's page — and never per row of a list: fifty
	// rows would be fifty requests for a verb a person uses on the record
	// they are looking at. Hidden until the API says the person may follow
	// it (or already does, so they can always leave); a 404 or a refusal
	// leaves it hidden — the menu is not where an error is explained.
	const followSurface = surface === 'pane' || surface === 'form';
	const [follow, setFollow] = useState(null);
	const [followBusy, setFollowBusy] = useState(false);
	// A boolean, not the array: a caller that rebuilt `exclude` every render
	// made this effect fetch, set state, re-render and fetch again, about
	// eighteen times a second (KPR-932).
	const followExcluded = (exclude || []).includes('follow');
	useEffect(() => {
		if (!followSurface || !saved || followExcluded) { setFollow(null); return undefined; }
		let active = true;
		getItemFollow(itemId)
			.then(data => { if (active) setFollow(data); })
			.catch(() => { if (active) setFollow(null); });
		return () => { active = false; };
	}, [followSurface, saved, itemId, followExcluded]);
	const toggleFollow = useCallback(async () => {
		if (!follow) return;
		const was = follow.followed;
		setFollowBusy(true);
		try {
			if (was) await unfollowItem(itemId);
			else await followItem(itemId);
			setFollow(current => ({ ...(current || {}), followed: !was }));
			const what = followedElementsText(follow.elements);
			message.success(was
				? 'You no longer follow this item.'
				: `You follow this item. You'll hear when ${what || 'it'} changes.`);
		} catch (error) {
			message.error(followRefusal(error));
		} finally {
			setFollowBusy(false);
		}
	}, [follow, itemId, message]);

	// The card's duplicate profiles — fetched once per collection and shared
	// by every row (useDuplicateProfiles), and only where Duplicate is on
	// offer at all.
	const cardDefinitions = useOptionalCardDefinitions();
	const cardCache = cardsById || cardDefinitions?.cardsById || null;
	const duplicateOffered = saved && canEdit && !!cardId && !(exclude || []).includes('duplicate');
	const { profiles: profileTemplates } = useDuplicateProfiles(collectionId, { enabled: duplicateOffered && !!collectionId });
	const duplicateProfiles = useMemo(() => (duplicateOffered
		? selectDuplicateProfiles({ collection, templates: profileTemplates, cardsById: cardCache, cardId })
		: []), [duplicateOffered, collection, profileTemplates, cardCache, cardId]);
	const defaultProfile = useMemo(() => defaultDuplicateProfile(duplicateProfiles), [duplicateProfiles]);
	const plainDuplicate = useCallback(
		() => navigate(...duplicateItemNavigation(collectionId, cardId, item)),
		[navigate, collectionId, cardId, item]
	);
	const runProfile = useCallback(template => launchDuplicateProfile(navigate, {
		collectionId, cardId, template, sourceItemId: itemId,
	}), [navigate, collectionId, cardId, itemId]);
	const runDuplicate = useCallback(
		() => (defaultProfile ? runProfile(defaultProfile) : plainDuplicate()),
		[defaultProfile, runProfile, plainDuplicate]
	);
	// The profiles as leaves: the default marked, then the way out of every
	// rule — the plain copy.
	const duplicateChoices = useMemo(() => (duplicateProfiles.length ? [
		...duplicateProfiles.map(({ template }) => ({
			key: `duplicate-profile-${normalizeId(template._id)}`,
			label: `${template.name || 'Untitled profile'}${template === defaultProfile ? ' (default)' : ''}`,
			onClick: () => runProfile(template),
		})),
		{ key: 'duplicate-plain', label: 'Plain duplicate', onClick: plainDuplicate },
	] : []), [duplicateProfiles, defaultProfile, runProfile, plainDuplicate]);

	// A RECORD OF RECORD (cardDefinitions.options.record) is corrected, not
	// edited, and a locked one is reopened by a manager. Both acts appear in
	// this menu only where the card says they mean something.
	// Read through the card's parents with whichever card cache there is (the
	// app's, when the surface passed none); only without any, or when the
	// cache does not hold this card, is the card in hand the chain.
	const recordPolicy = useMemo(
		() => recordPolicyFor(cardCache && cardId && cardCache[cardId] ? cardCache : (card && cardId ? { [cardId]: card } : {}), cardId),
		[ cardCache, card, cardId ]
	);
	// The card's delete policy, as DELETE /api/items/{id} applies it:
	// `never` refuses everyone, `manage-with-reason` a non-manager and a
	// manager who gives no reason.
	const deletePolicy = recordPolicy?.delete || 'allowed';
	const reasonRequired = deletePolicy === 'manage-with-reason';
	const computedLock = useMemo(() => lockStateFor(item, recordPolicy), [ item, recordPolicy ]);
	const lockState = useMemo(
		() => (typeof lockedOverride === 'boolean' ? { ...computedLock, locked: lockedOverride } : computedLock),
		[ computedLock, lockedOverride ]
	);
	// COPY TO… (KPR-1024): a copy made with it stands in for its original in
	// its copier's combined lists, or not — the copy's own switch, offered to
	// the person who made it (its owner; the API refuses anyone else).
	const { copiedFrom, ownCopy, standInBusy, toggleStandIn } = useStandInCopy({ item, saved, itemId, message, onItemUpdated, onRelated });
	const locksRecords = cardLocksRecords(recordPolicy);
	const changeCardState = itemChangeCardState({ canEdit, locked: lockState.locked, lockReason: lockState.reason, linkedReason: linkedReadOnlyReason(item) });
	const superseded = !!item?.supersededBy;

	const openModal = useCallback((key) => {
		setMounted(prev => (prev.has(key) ? prev : new Set([...prev, key])));
		setActiveModal(key);
	}, []);
	const closeModal = useCallback(() => setActiveModal(null), []);

	const confirmDelete = useCallback(() => {
		if (!saved) return;
		let reason = '';
		let dialog = null;
		const onReason = value => {
			reason = value;
			if (dialog) dialog.update({ okButtonProps: { danger: true, disabled: !reason.trim() } });
		};
		const body = count => (
			<ItemDeleteBody text={itemDeleteConfirmText(count)} reasonRequired={reasonRequired} onReason={onReason} />
		);
		dialog = confirmDestructive(modal, {
			title: `Delete “${title}”?`,
			content: body(0),
			okButtonProps: { disabled: reasonRequired },
			onOk: async () => {
				const why = reason.trim();
				try {
					if (why) await api.delete(`${API_ITEMS_URL}/${itemId}`, { data: { reason: why } });
					else await api.delete(`${API_ITEMS_URL}/${itemId}`);
					message.success('Item deleted.');
					if (typeof onDeleted === 'function') onDeleted(itemId);
				} catch (error) {
					// The card's policy refusing (item_undeletable, reason_required)
					// says why in the API's own words.
					const code = error?.response?.data?.code;
					message.error(code && error.response.data.message
						? error.response.data.message
						: 'Something went wrong. The item may not have been deleted.');
				}
			},
		});
		// Name the attachments that go with it (delete audit F3). The dialog
		// opens at once and the sentence gains the count when it arrives; only
		// asked where the collection has attachments on, and a failed count
		// leaves the plain sentence — it never holds the delete up.
		if (collection?.allowAttachments === true) {
			getItemAttachments(itemId)
				.then(rows => {
					const count = Array.isArray(rows) ? rows.length : 0;
					if (count) dialog.update({ content: body(count) });
				})
				.catch(() => {});
		}
	}, [saved, modal, title, itemId, message, onDeleted, collection, reasonRequired]);

	const handleRelate = useCallback(async (relatedItemId) => {
		if (!saved) return;
		setRelating(true);
		try {
			await createItemRelationship({ itemId, relatedItemId });
			message.success('Item related.');
			closeModal();
			if (typeof onRelated === 'function') onRelated();
		} catch (error) {
			const status = error?.response?.status;
			if (status === 409) message.warning('These items are already related.');
			else if (status === 400) message.error('Those items can\'t be related.');
			else message.error('Unable to relate that item.');
		} finally {
			setRelating(false);
		}
	}, [saved, itemId, message, closeModal, onRelated]);

	// "Tag: …" entries name the item's tags as its chips do: the vocabulary of
	// the item's collection (asked for only inside a filterable list) and my
	// private tags.
	const { tags: tagVocabulary } = useCollectionTags(normalizeId(item?.collection_id) || collectionId, { enabled: Boolean(applyValueFilter) });
	const myPrivateTags = useOptionalTags()?.tags || null;
	const filterChildren = useMemo(() => {
		if (!applyValueFilter) return [];
		const tagEntries = itemTagEntries(item, { vocabulary: tagVocabulary, privateTags: myPrivateTags, titles: rememberedItemTagTitles() });
		return itemValueFilters({ item, card, elementDefs, resolveLookupValue, facetFor, tagEntries })
			.flatMap((filter, index) => {
				const entries = [{
					key: `filter-by-${index}`,
					label: describeValueFilter(filter),
					onClick: () => applyValueFilter(filter),
				}];
				// A lookup a FACET covers filters across every card by default;
				// the field's own condition stays one line below, named for what
				// it narrows to, so "just this column" is never unreachable.
				if (filter.alternate) {
					entries.push({
						key: `filter-by-${index}-only`,
						label: `${describeValueFilter(filter.alternate)} · only this field`,
						onClick: () => applyValueFilter(filter.alternate),
					});
				}
				return entries;
			});
	}, [applyValueFilter, facetFor, item, card, elementDefs, resolveLookupValue, tagVocabulary, myPrivateTags]);

	// Use as a tag / Stop using as a tag… (T4d): offered only where the item's
	// card is in Chosen items mode and the person manages its collection.
	const ownTag = useItemOwnTag({
		item,
		card,
		title,
		cardType,
		collection,
		canManage,
		enabled: saved && !(exclude || []).includes('own-tag'),
		onItemUpdated,
	});
	const ownTagAction = ownTag.action;

	// CARD ACTIONS (KPR-551, reshaped by KPR-1342): the buttons a manager
	// defined on this record's card. On every surface the menu has ONE
	// Actions entry, in the Record group, whose submenu lists EVERY action of
	// the card in the card's order, whatever its condition (the owner's ruling
	// q05 as corrected: knowledge/rules/card-action-buttons-ruling.md); one
	// whose condition does not hold here is listed disabled with its reason
	// as a line under its name. The item page's header also shows, as buttons,
	// the ones that apply (ItemForm, `headerActions` below); rows, tiles and the
	// detail pane never do (d52). The actions come from ONE per-collection read
	// shared by every row (hooks/useCardActions), and only for a person who can
	// edit the record — the API lists only what its caller could press, and
	// presses as them. A row of another collection (a linked record) asks that
	// one. Whether a conditioned action applies is the record's own read
	// (hooks/useRecordCardActions): asked at once where the record is the
	// surface (the form, the pane), and when the ⋯ opens on a row or a tile.
	const actionsExcluded = (exclude || []).includes('card-actions');
	const actionsOffered = saved && canEdit && !!cardId && !actionsExcluded;
	const actionsCollectionId = normalizeId(item?.collection_id) || normalizeId(collectionId);
	const { actions: collectionCardActions } = useCardActions(actionsCollectionId, { enabled: actionsOffered });
	const cardActionRows = useMemo(
		() => (actionsOffered ? actionsForCard(collectionCardActions, cardId) : []),
		[actionsOffered, collectionCardActions, cardId]
	);
	const conditional = anyConditional(cardActionRows);
	// The record is the surface (its form, its pane): its answer is read at
	// once and again whenever it changes. A row or a tile reads it when its ⋯
	// opens (`menuOpened`), every time it opens.
	const ownSurface = surface === 'form' || surface === 'pane' || surface === 'inline';
	const [menuOpens, setMenuOpens] = useState(0);
	const onMenuOpenChange = useCallback((open) => { if (open) setMenuOpens(n => n + 1); }, []);
	const { answers: actionAnswers, failed: answersFailed, reload: reloadAnswers } = useRecordCardActions(itemId, {
		enabled: conditional && (ownSurface || menuOpens > 0),
		version: ownSurface ? `${item?.updatedAt || ''}` : `${menuOpens}:${item?.updatedAt || ''}`,
	});
	const [askingAction, setAskingAction] = useState(null);
	// One press at a time on this record: a second click while one is in
	// flight is dropped here, and a resend with the same key would be
	// answered by the API without writing again.
	const pressing = useRef(false);
	const cardLabelOf = useCallback(id => {
		const found = cardCache?.[id];
		return found?.label?.singular || found?.name || 'item';
	}, [cardCache]);
	const reportPress = useCallback(answer => {
		const { created } = summarizeWritten(answer);
		if (answer?.ok === false && answer?.partial) {
			message.warning(partialSentence(answer));
			return;
		}
		const links = created.map(entry => (
			<span key={entry.id}>
				{' '}
				<Link to={`/collections/${actionsCollectionId}/items/${entry.id}`}>Open the new {cardLabelOf(entry.cardId)}</Link>
			</span>
		));
		message.success(<span>{pressSentence(answer)}{links}</span>);
	}, [message, actionsCollectionId, cardLabelOf]);
	// The press itself. Resolves with the answer, rejects with the refusal.
	const press = useCallback(async (row, inputs) => {
		if (pressing.current) return null;
		pressing.current = true;
		try {
			let answer;
			try {
				answer = await pressCardAction(itemId, row._id, { inputs, key: newPressKey() });
			} catch (error) {
				// The page offered it, the server judged the record otherwise (it
				// changed since): read the record's answer again, so the header
				// and the menu stop offering it (KPR-1342).
				if (isNotApplicable(error)) reloadAnswers();
				throw error;
			}
			reportPress(answer);
			// The press changed the record: the surface shows it as it stands now.
			// A list puts the fresh record in the row's place; a page re-reads.
			if (typeof onItemUpdated === 'function') {
				try {
					const fresh = await api.get(`${API_ITEMS_URL}/${itemId}`);
					if (fresh?.data) onItemUpdated(fresh.data);
				} catch {
					// Not read: the row keeps what it had until the next load.
				}
			}
			if (typeof onRelated === 'function') onRelated();
			if (typeof onPressed === 'function') onPressed(answer);
			return answer;
		} finally {
			pressing.current = false;
		}
	}, [itemId, reportPress, onItemUpdated, onRelated, onPressed, reloadAnswers]);
	const pressFromMenu = useCallback(row => {
		if (pressing.current) return;
		if (row.inputs?.length) { setAskingAction(row); return; }
		const run = () => press(row, {}).catch(error => { message.error(pressRefusal(error)); });
		if (row.button?.confirm) {
			modal.confirm({
				title: actionLabel(row),
				content: row.button.confirm,
				okText: actionLabel(row),
				cancelText: 'Cancel',
				className: 'card-action-confirm',
				// Held open until the press answers, so the button cannot be clicked twice.
				onOk: run,
			});
			return;
		}
		run();
	}, [press, message, modal]);

	// The submenu's entries: every action, in the card's order. One that does
	// not apply is not antd-disabled — a disabled menu item cannot take focus,
	// so a keyboard or a screen reader would never reach its reason (the
	// KPR-1211 trap) — but `unavailable`: muted, aria-disabled, its reason a
	// line under its name, and pressing it says the reason again instead of
	// running. While a conditioned one's answer is on its way it says so.
	const cardActionEntries = useMemo(() => cardActionRows.map((row) => {
		const standing = actionStanding(row, actionAnswers, { failed: answersFailed });
		const key = `card-action-${row._id}`;
		const icon = <LibraryIcon name={row.button?.icon} fallback={LIGHTNING} />;
		const label = actionLabel(row);
		if (standing.state === 'unavailable') {
			return { key, icon, label, unavailable: standing.reason, onClick: () => message.info(`${label}: ${standing.reason}`) };
		}
		if (standing.state === 'checking') {
			return { key, icon, label, unavailable: 'Checking whether it applies…', checking: true, onClick: () => {} };
		}
		return { key, icon, label, title: row.name && row.name !== label ? row.name : undefined, onClick: () => pressFromMenu(row) };
	}), [cardActionRows, actionAnswers, answersFailed, pressFromMenu, message]);
	// The item page's header buttons (KPR-1342): only the actions known to
	// apply to this record — one without a condition, or one the record's
	// answer says holds. Unknown (not answered, or the read failed) is not
	// shown: the header never offers a button that may not run. The surface
	// decides how many fit.
	const headerActions = useMemo(() => {
		if (surface !== 'form') return [];
		return cardActionRows
			.filter(row => !row.conditional || actionAnswers?.get(normalizeId(row._id))?.applies === true)
			.map(row => ({
				key: `card-action-${row._id}`,
				label: actionLabel(row),
				icon: <LibraryIcon name={row.button?.icon} fallback={LIGHTNING} />,
				onClick: () => pressFromMenu(row),
			}));
	}, [surface, cardActionRows, actionAnswers, pressFromMenu]);

	const actions = useMemo(() => {
		const excluded = new Set(exclude || []);
		return [
			{
				key: 'open',
				group: 'navigate',
				glyph: 'open',
				label: 'Open item',
				hidden: surface === 'form' || !saved,
				onClick: () => navigate(`/collections/${collectionId}/items/${itemId}`),
			},
			{
				// KPR-491: a Symptom walks its diagnostic tree one step at a time.
				key: 'walk',
				group: 'navigate',
				glyph: 'walk',
				label: 'Start walk-through',
				hidden: !saved || !isSymptom(card) || excluded.has('walk'),
				onClick: () => openModal('walk'),
			},
			{
				key: 'card',
				group: 'navigate',
				glyph: 'card',
				label: 'Go to card definition',
				hidden: !cardId,
				onClick: () => navigate(cardPagePath(normalizeId(card?._id) === cardId ? card : cardId, collectionId)),
			},
			{
				// ONE entry, every action of the card beneath it (KPR-1342).
				key: 'card-actions',
				group: 'record',
				icon: <LIGHTNING />,
				label: 'Actions',
				hidden: !cardActionEntries.length,
				children: cardActionEntries,
			},
			{
				key: 'duplicate',
				group: 'record',
				glyph: 'duplicate',
				label: 'Duplicate',
				title: defaultProfile ? `Duplicates with “${defaultProfile.name}”` : undefined,
				hidden: !saved || !canEdit || !cardId,
				onClick: runDuplicate,
			},
			{
				key: 'duplicate-as',
				group: 'record',
				glyph: 'duplicate',
				label: 'Duplicate as…',
				hidden: !saved || !canEdit || !cardId || !duplicateChoices.length,
				children: duplicateChoices,
			},
			{
				// Into one of the person's own collections with the same card
				// (KPR-1024): read is enough here — the target's write is the API's.
				key: 'copy-to',
				group: 'record',
				glyph: 'duplicate',
				label: 'Copy to…',
				hidden: !saved || !cardId,
				onClick: () => openModal('copyTo'),
			},
			{
				// Change card (KPE-028, KPR-710): this item to another card. Shut, the
				// reason shows in the menu (a title is out of a touch's reach).
				key: 'change-card',
				group: 'record',
				glyph: 'card',
				label: CHANGE_CARD_LABEL,
				hidden: !saved || !cardId || !collectionId || excluded.has('change-card'),
				unavailable: changeCardState.disabled ? changeCardState.reason : undefined,
				onClick: () => {
					if (changeCardState.disabled) { message.info(`${CHANGE_CARD_LABEL}: ${changeCardState.reason}`); return; }
					const path = changeCardPath(collectionId, { card: cardId, ids: [itemId] });
					if (path) navigate(path);
				},
			},
			{
				key: 'stand-in',
				group: 'record',
				glyph: 'visibility',
				label: copiedFrom?.standIn ? 'Show the original again' : 'Show my copy instead of the original',
				hidden: !saved || !ownCopy || !canEdit,
				disabled: standInBusy,
				onClick: toggleStandIn,
			},
			{
				key: 'quick-add',
				group: 'record',
				glyph: 'quickAdd',
				label: 'Save as quick add…',
				// Captures the values on THIS surface as Fixed defaults — on the
				// form, whatever is typed right now, saved or not. Needs the
				// collection (templates nest under it) and a card to bind to.
				hidden: !canEdit || !cardId || !collectionId,
				onClick: () => openModal('quickAdd'),
			},
			{
				key: 'pin',
				group: 'record',
				glyph: 'pin',
				// The person's own shortcut on their Home page — any record
				// they can read, whichever collection it lives in.
				label: pinned ? 'Unpin from Home' : 'Pin to Home',
				hidden: !saved || !favorites?.togglePin,
				onClick: () => favorites.togglePin(PIN_KIND.ITEM, { targetId: itemId, collectionId: normalizeId(item?.collection_id) || collectionId }),
			},
			{
				key: 'pin-tile',
				group: 'record',
				glyph: 'pin',
				// A tile of one to three of its values on Home (KPR-1127),
				// read each time Home opens.
				label: tiled ? 'Edit Home tile…' : 'Pin as tile…',
				hidden: !saved || !favorites?.saveTile || !cardId,
				onClick: () => openModal('tile'),
			},
			{
				key: 'follow',
				group: 'record',
				glyph: 'follow',
				label: follow?.followed ? 'Unfollow' : 'Follow',
				// Offered while the person may follow it, and always to someone
				// who already does (a follow outlives a collection turning
				// follows off, and they must be able to leave).
				hidden: !follow || !(follow.followed || follow.canFollow),
				disabled: followBusy,
				onClick: toggleFollow,
			},
			{
				key: 'tag',
				group: 'record',
				glyph: 'tag',
				label: 'Tag…',
				// Read is enough (private tags). An unsaved item is tagged by
				// the form's own row, which is what `onOpenTags` opens.
				hidden: !(saved || typeof onOpenTags === 'function'),
				onClick: () => (typeof onOpenTags === 'function' ? onOpenTags() : openModal('tags')),
			},
			{
				key: 'own-tag',
				group: 'record',
				glyph: 'tag',
				label: ownTagAction?.label || 'Use as a tag',
				hidden: !ownTagAction,
				onClick: () => ownTagAction?.onClick(),
			},
			{
				key: 'filter-by',
				group: 'record',
				glyph: 'filter',
				label: 'Filter by…',
				// No list to narrow, or nothing on this record worth narrowing
				// by (every value empty, every field a paragraph).
				hidden: !filterChildren.length,
				children: filterChildren,
			},
			{
				key: 'relate',
				group: 'record',
				glyph: 'relate',
				label: 'Relate item…',
				hidden: !saved || !canEdit,
				onClick: () => (typeof onOpenRelated === 'function' ? onOpenRelated() : openModal('relate')),
			},
			{
				key: 'attachments',
				group: 'record',
				glyph: 'attach',
				label: 'Attachments…',
				hidden: !saved || collection?.allowAttachments !== true,
				onClick: () => (typeof onOpenAttachments === 'function' ? onOpenAttachments() : openModal('attachments')),
			},
			{
				key: 'audience',
				group: 'record',
				glyph: 'visibility',
				label: 'Who can see this',
				hidden: !saved,
				onClick: () => openModal('audience'),
			},
			{
				key: 'history',
				group: 'record',
				icon: <HISTORY />,
				label: 'History…',
				// The API gates an item's history on write (a reader would see
				// values from before their grant matched), so the door follows
				// canEdit.
				hidden: !saved || !canEdit,
				onClick: () => openModal('history'),
			},
			{
				key: 'share',
				group: 'share',
				glyph: 'share',
				label: 'Share item',
				hidden: !saved,
				onClick: () => openModal('share'),
			},
			{
				key: 'export',
				group: 'share',
				glyph: 'export',
				label: 'Export…',
				hidden: !saved,
				onClick: () => openModal('export'),
			},
			{
				key: 'print',
				group: 'share',
				glyph: 'print',
				label: 'Print…',
				// Paper for ONE record, so it needs a saved one and the
				// collection the sheet is printed from.
				hidden: !saved || !collectionId,
				onClick: () => openModal('print'),
			},
			{
				key: 'correct',
				group: 'record',
				glyph: 'edit',
				label: 'Correct this record…',
				// A correction restates a record that is finished: offered
				// where the card says its items are records, and never on one
				// that has already been corrected (the correction is what a
				// second fix restates).
				hidden: !saved || !canEdit || !locksRecords || superseded,
				onClick: () => openModal('correct'),
			},
			{
				key: 'unlock',
				group: 'record',
				glyph: 'visibility',
				label: 'Unlock…',
				hidden: !saved || !lockState.locked || !canManage,
				onClick: () => openModal('unlock'),
			},
			{
				key: 'delete',
				group: 'destructive',
				glyph: 'delete',
				label: 'Delete item…',
				// A `never` card's record is refused to everyone: the door is
				// not offered where the API would only say no. A
				// `manage-with-reason` one stays offered — not every surface
				// knows `canManage`, and a non-manager hears the API's own
				// "Only a manager can delete a record of this card."
				hidden: !saved || !canEdit || deletePolicy === 'never',
				onClick: confirmDelete,
			},
		].filter(action => !excluded.has(action.key));
	}, [
		exclude, surface, saved, cardId, card, canEdit, canManage, locksRecords, superseded, deletePolicy,
		lockState.locked, collection?.allowAttachments,
		navigate, collectionId, itemId, item, onOpenRelated, onOpenAttachments, onOpenTags,
		openModal, confirmDelete, filterChildren, pinned, tiled, favorites,
		runDuplicate, defaultProfile, duplicateChoices, ownTagAction,
		follow, followBusy, toggleFollow, cardActionEntries,
		copiedFrom, ownCopy, standInBusy, toggleStandIn, changeCardState.disabled, changeCardState.reason, message,
	]);

	const allActions = useMemo(
		() => (Array.isArray(extraActions) && extraActions.length ? [...extraActions, ...actions] : actions),
		[extraActions, actions]
	);
	const menu = useMemo(() => buildActionMenu(allActions), [allActions]);

	// The split button's arrow (Duplicate ▾): every profile, then the plain
	// copy — null when the card has no profiles, so the surface keeps its
	// plain Duplicate button.
	const duplicateMenu = useMemo(() => {
		if (!duplicateChoices.length) return null;
		const byKey = new Map(duplicateChoices.map(choice => [choice.key, choice]));
		return {
			className: 'action-menu',
			items: duplicateChoices.map(choice => ({ key: choice.key, label: choice.label })),
			onClick: ({ key, domEvent }) => {
				domEvent?.stopPropagation?.();
				byKey.get(key)?.onClick();
			},
		};
	}, [duplicateChoices]);

	const run = useCallback((key) => {
		const action = actions.find(entry => entry.key === key && !entry.hidden);
		if (action) action.onClick();
	}, [actions]);

	// Rebuilt when the item's short code arrives (hooks/useRecordCodes).
	const codesVersion = useRecordCodesVersion();
	const shareUrl = useMemo(() => buildItemShareUrl(collectionId, itemId), [collectionId, itemId, codesVersion]); // eslint-disable-line react-hooks/exhaustive-deps
	const exportTarget = useMemo(() => (saved ? { kind: 'item', itemId } : null), [saved, itemId]);

	const modals = (
		<>
			{ownTag.modal}
			{askingAction ? (
				<CardActionInputsModal
					open
					row={askingAction}
					onCancel={() => setAskingAction(null)}
					onPress={async values => {
						const answer = await press(askingAction, values);
						if (answer) setAskingAction(null);
					}}
				/>
			) : null}
			{mounted.has('walk') ? (
				<TroubleshootWalkthrough
					open={activeModal === 'walk'}
					onClose={closeModal}
					symptom={item}
					title={title}
					collectionId={collectionId}
				/>
			) : null}
			{mounted.has('share') ? (
				<ItemShareModal
					open={activeModal === 'share'}
					onClose={closeModal}
					card={card}
					cardType={cardType}
					title={title}
					shareUrl={shareUrl}
					itemId={itemId}
					collectionId={collectionId}
					collection={collection}
				/>
			) : null}
			{mounted.has('audience') ? (
				<ItemAudienceModal
					open={activeModal === 'audience'}
					onClose={closeModal}
					itemId={itemId}
					itemTitle={title}
				/>
			) : null}
			{mounted.has('export') ? (
				<ExportModal
					open={activeModal === 'export'}
					onClose={closeModal}
					target={exportTarget}
					title="Export item"
					baseName={title}
					supportsAttachmentOptions
				/>
			) : null}
			{mounted.has('print') ? (
				<PrintLayoutPicker
					open={activeModal === 'print'}
					onClose={closeModal}
					cardId={item?.card_id}
					collectionId={collectionId}
					itemId={itemId}
				/>
			) : null}
			{mounted.has('relate') ? (
				<RelateItemModal
					open={activeModal === 'relate'}
					onCancel={closeModal}
					onSelect={handleRelate}
					collectionId={collectionId}
					excludeItemId={itemId}
					linkedItemIds={[]}
					saving={relating}
				/>
			) : null}
			{mounted.has('tile') ? (
				<RecordTileModal
					open={activeModal === 'tile'}
					onClose={closeModal}
					target={tileTarget}
					cardId={cardId}
					name={title}
				/>
			) : null}
			{mounted.has('history') ? (
				<HistoryModal
					open={activeModal === 'history'}
					onClose={closeModal}
					kicker={cardType}
					badge={<RecordBadge kind="item" record={card} title={title} collection={rowOwnCollection(item) || normalizeId(collectionId) || null} />}
					fetchPage={params => fetchItemHistory(itemId, params)}
					onReverted={onRelated}
				/>
			) : null}
			{mounted.has('tags') ? (
				<ItemTagsModal
					open={activeModal === 'tags'}
					onClose={closeModal}
					item={item}
					card={card}
					cardType={cardType}
					title={title}
					collection={collection}
					collectionId={collectionId}
					canEdit={canEdit}
					onItemUpdated={onItemUpdated}
				/>
			) : null}
			{mounted.has('correct') ? (
				<ItemCorrectionModal
					open={activeModal === 'correct'}
					item={item}
					card={card}
					elementDefs={elementDefs}
					onClose={closeModal}
					onCorrected={onRelated}
				/>
			) : null}
			{mounted.has('unlock') ? (
				<ItemUnlockModal
					open={activeModal === 'unlock'}
					item={item}
					onClose={closeModal}
					onUnlocked={onUnlocked || onRelated}
				/>
			) : null}
			{mounted.has('copyTo') ? (
				<CopyItemModal
					open={activeModal === 'copyTo'}
					onClose={closeModal}
					item={item}
					card={card}
					cardType={cardType}
					title={title}
				/>
			) : null}
			{mounted.has('quickAdd') ? (
				<QuickAddTemplateModal
					open={activeModal === 'quickAdd'}
					onCancel={closeModal}
					onSaved={closeModal}
					mode="capture"
					collectionId={collectionId}
					collection={collection}
					card={card}
					initialElements={item?.elements || {}}
				/>
			) : null}
			{mounted.has('attachments') ? (
				<Modal
					open={activeModal === 'attachments'}
					onCancel={closeModal}
					footer={null}
					title="Attachments"
					destroyOnClose
				>
					<div className="modal-identity">
						<RecordIdentity
							kicker={cardType}
							badge={<RecordBadge kind="item" record={card} title={title} collection={rowOwnCollection(item) || normalizeId(collectionId) || null} />}
						/>
					</div>
					<AttachmentsSection
						subjectType="item"
						subjectId={itemId}
						readOnly={!canEdit}
						locked={lockState.locked}
						variant="embedded"
					/>
				</Modal>
			) : null}
		</>
	);

	return { actions: allActions, menu, modals, run, title, cardType, duplicateMenu, defaultDuplicateProfile: defaultProfile, headerActions, onMenuOpenChange };
}

// Surfaces where the record is the page: mounted eagerly (ownSurface in useItemActions).
const OWN_SURFACES = new Set(['form', 'pane', 'inline']);

/**
 * The ⋯ trigger for an item plus the modals its actions open. Drop it on any
 * surface; pass `trigger` to replace the default icon button.
 *
 * It renders the trigger alone until the first time it is opened (KPR-1547).
 * useItemActions runs some fifty hooks, so a page of fifty rows each carrying
 * a full hook set re-evaluated all of them for every re-render cause. The body
 * (ItemActionsMenuBody, which calls useItemActions) mounts on that first open
 * and stays mounted, so the modals it opened keep their close animation and
 * the `mounted` set keeps working.
 *
 * A surface that is the record itself (its pane, its form, its inline view)
 * mounts the body at once: it reads the follow state and the card action
 * answers when it appears, not when the menu opens, and there is one of it.
 */
export default function ItemActionsMenu({
	trigger = null,
	buttonProps = {},
	placement = 'bottomRight',
	className = '',
	...actionProps
}) {
	const [opened, setOpened] = useState(() => OWN_SURFACES.has(actionProps.surface ?? 'pane'));
	// Whether the body mounted from a click on the trigger, which asked for the menu.
	const [clicked, setClicked] = useState(false);
	const button = trigger || (
		<Button
			icon={<KEBAB />}
			aria-label="Item actions"
			className={className || undefined}
			{...buttonProps}
		/>
	);

	if (opened) {
		return <ItemActionsMenuBody button={button} placement={placement} actionProps={actionProps} startOpen={clicked} />;
	}
	return (
		<Dropdown trigger={['click']} placement={placement} open={false} onOpenChange={open => { if (open) { setClicked(true); setOpened(true); } }}>
			{button}
		</Dropdown>
	);
}

/**
 * The menu and its modals, mounted by ItemActionsMenu on its first open. It
 * opens at once, since the click that mounted it asked for the menu.
 */
export function ItemActionsMenuBody({ button, placement, actionProps, startOpen = false }) {
	const { menu, modals, onMenuOpenChange } = useItemActions(actionProps);
	const [open, setOpen] = useState(startOpen);
	// The open that mounted this counts for the per-open card action answers.
	useEffect(() => { if (startOpen) onMenuOpenChange(true); }, [startOpen, onMenuOpenChange]);
	if (!menu) return null;

	return (
		<>
			<Dropdown
				trigger={['click']}
				placement={placement}
				menu={menu}
				open={open}
				onOpenChange={next => { setOpen(next); onMenuOpenChange(next); }}
			>
				{button}
			</Dropdown>
			{modals}
		</>
	);
}
